import { useMemo } from 'react';
import { Tabs } from 'antd';
import { useSearchParams } from 'react-router-dom';

import { useTrainingSections } from '@/entities/training/api/libraryApi';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import type { SectionKey } from '@/entities/training/model/librarySchema';
import { sectionKeyLabels } from '@/entities/training/model/librarySchema';
import { InternshipTab } from './InternshipTab';
import { LibraryTab } from './LibraryTab';
import { MatrixTab } from './MatrixTab';
import { MyTrainingTab } from './MyTrainingTab';
import { RecordsTab } from './RecordsTab';
import { RequestsTab } from './RequestsTab';

export function Training() {
  const [params, setParams] = useSearchParams();
  const tabFromUrl = params.get('tab') ?? '';

  const { data: sections, isPending: sectionsPending } = useTrainingSections();
  const allowedKeys = useMemo(() => new Set(sections?.map((s) => s.key)), [sections]);

  const focusEmployeeId = useMemo(() => {
    const raw = params.get('employeeId');
    if (!raw) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [params]);

  const visibleTabs = useMemo(() => {
    const allTabs = [
      {
        key: 'my_training',
        sectionKey: 'my_training' as SectionKey,
        label: sectionKeyLabels.my_training,
        children: <MyTrainingTab />,
      },
      {
        key: 'records',
        sectionKey: 'employees_training_table' as SectionKey,
        label: sectionKeyLabels.employees_training_table,
        children: <RecordsTab focusEmployeeId={focusEmployeeId} />,
      },
      {
        key: 'matrix',
        sectionKey: 'training_matrix' as SectionKey,
        label: sectionKeyLabels.training_matrix,
        children: <MatrixTab />,
      },
      {
        key: 'requests',
        sectionKey: 'training_requests' as SectionKey,
        label: sectionKeyLabels.training_requests,
        children: <RequestsTab />,
      },
      {
        key: 'library',
        sectionKey: 'training_library' as SectionKey,
        label: sectionKeyLabels.training_library,
        children: <LibraryTab />,
      },
      {
        key: 'internship',
        sectionKey: 'internships' as SectionKey,
        label: sectionKeyLabels.internships,
        children: <InternshipTab />,
      },
    ];

    if (!sections) return [];
    return allTabs.filter((t) => allowedKeys.has(t.sectionKey));
  }, [sections, allowedKeys, focusEmployeeId]);

  const firstTabKey = visibleTabs[0]?.key ?? 'my_training';

  const activeKey = useMemo(() => {
    if (tabFromUrl && visibleTabs.some((t) => t.key === tabFromUrl)) return tabFromUrl;
    return firstTabKey;
  }, [tabFromUrl, visibleTabs, firstTabKey]);

  const handleTabChange = (key: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  };

  if (sectionsPending) {
    return <PageSpinner />;
  }

  return <Tabs activeKey={activeKey} onChange={handleTabChange} items={visibleTabs} />;
}
